import { Popover, Portal } from "@headlessui/react";
import { CogIcon } from "@heroicons/react/outline";
import { compact, isEqual, uniq } from "lodash";
import { Dispatch, MouseEvent, SetStateAction, useState } from "react";
import { MdOutlineBookmarks } from "react-icons/md";
import { TbCaretDownFilled, TbCaretUpFilled } from "react-icons/tb";
import { usePopper } from "react-popper";

import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppLink from "@components/link/link";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
  ToggleButtonGroupType,
} from "@components/toggle-button-group/toggle-button-group";
import { classNames } from "@helpers/css";

import { ExplorerFilterType, getExplorerFiltersUrl } from "../helpers";
import { BookmarkFilterEnum } from "./bookmark-constants";
import { getExplorerBookmarks } from "./bookmark-popover-helpers";

export type ExplorerBookmarkLinks = {
  title: string;
  filters: ExplorerFilterType;
  showInSlashCommand: boolean;
  default: boolean;
}[];

const ExplorerBookmarkPopover = ({
  filters,
  onClickBookmark,
  children = null,
  className = "",
}: {
  filters?: ExplorerFilterType;
  className?: string;
  children?:
    | null
    | ((props: {
        setReferenceElement: Dispatch<SetStateAction<HTMLElement | null>>;
      }) => JSX.Element);
  onClickBookmark?: (filters: ExplorerFilterType) => void;
}) => {
  const label = useLabel();
  const [bookmarkFilter, setBookmarkFilter] = useState(
    BookmarkFilterEnum.onlyYou,
  );
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null,
  );
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "right-start",
  });
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();

  const bookmarksByGroup = getExplorerBookmarks(bookmarkFilter);

  const handleClickToggleGroup = (e: MouseEvent<HTMLButtonElement>) => {
    const groupId = e.currentTarget.dataset.groupid;
    const matchingGroup = bookmarksByGroup.find(({ id }) => id === groupId);
    const newValue = matchingGroup?.expanded
      ? uiPreferenceCache.explorerCollapsedGroupIds.concat(groupId)
      : uiPreferenceCache.explorerCollapsedGroupIds.filter(
          (id: any) => id !== groupId,
        );
    saveUiPreference({
      explorerCollapsedGroupIds: uniq(compact(newValue)) || [],
    });
  };

  const handleClickBookmarkContext = (
    button: ToggleButtonGroupType<BookmarkFilterEnum>,
    e: MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (button.value) {
      setBookmarkFilter(button.value);
    }
  };

  // RENDER
  return (
    <Popover className={classNames("relative", className)}>
      {children ? (
        children({ setReferenceElement })
      ) : (
        <Popover.Button className="text-gray-500 hover:text-gray-800 p-0.5 focus:outline-hidden focus:ring-0 rounded-sm hover:bg-black/5">
          <MdOutlineBookmarks className="h-4 w-4" />
        </Popover.Button>
      )}
      <Portal>
        <Popover.Panel
          className="absolute left-0 z-dropdown bg-white rounded-lg p-3 border shadow-lg w-84 min-h-48 max-h-96 overflow-y-scroll"
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          <div
            aria-label="Explorer bookmarks container"
            className="flex flex-col gap-2 tracking-tight text-sm"
          >
            <div className="flex mb-1 justify-between">
              <ToggleButtonGroup<BookmarkFilterEnum>
                theme={ToggleButtonGroupTheme.roundedCompact}
                onClick={handleClickBookmarkContext}
                buttons={[
                  {
                    active: bookmarkFilter === BookmarkFilterEnum.onlyYou,
                    title: "My bookmarks",
                    value: BookmarkFilterEnum.onlyYou,
                  },
                  {
                    active: bookmarkFilter === BookmarkFilterEnum.organization,
                    title: label("organization", { capitalize: true }),
                    value: BookmarkFilterEnum.organization,
                  },
                  {
                    active: bookmarkFilter === BookmarkFilterEnum.default,
                    title: "Default",
                    value: BookmarkFilterEnum.default,
                  },
                ]}
              />
              <Button
                size={ButtonSize.medium}
                icon={CogIcon}
                to="/explorer/bookmarks"
                theme={buttonTheme.iconGray}
                tooltip="Manage bookmarks"
              />
            </div>
            {bookmarksByGroup.length > 0 &&
              bookmarksByGroup.map((group) => (
                <div key={group.id}>
                  <button
                    className="flex items-center gap-1 hover:bg-black/5 rounded-lg p-0.5 px-1.5"
                    onClick={handleClickToggleGroup}
                    data-groupid={group.id}
                  >
                    {group.icon}
                    <div className="flex items-center text-gray-900 font-medium gap-0.5">
                      {label(group.label, {
                        capitalize: true,
                        pluralize: true,
                      })}
                      {group.expanded ? (
                        <TbCaretDownFilled className="w-4 h-4 text-gray-500" />
                      ) : (
                        <TbCaretUpFilled className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                  </button>
                  {group.expanded && (
                    <ul className="mb-1 ml-6 pl-4 list-disc">
                      {group.links.map((link) => (
                        <li className="text-gray-700" key={link.title}>
                          {onClickBookmark ? (
                            <button
                              onClick={() => onClickBookmark(link.filters)}
                              className={classNames(
                                "hover:underline",
                                isEqual(link.filters, filters) &&
                                  "text-blue-link underline font-medium",
                              )}
                            >
                              {link.title}
                            </button>
                          ) : (
                            <AppLink
                              to={getExplorerFiltersUrl(link.filters)}
                              className={classNames(
                                "hover:underline",
                                isEqual(link.filters, filters) &&
                                  "text-blue-link underline font-medium",
                              )}
                            >
                              {link.title}
                            </AppLink>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            {bookmarksByGroup.length === 0 && (
              <div className="text-gray-500 text-sm">No bookmarks.</div>
            )}
          </div>
        </Popover.Panel>
      </Portal>
    </Popover>
  );
};

export default ExplorerBookmarkPopover;
